import React, { useState } from 'react';
import { X, CheckSquare, Square, Clock, Tag, User, AlertCircle, MessageSquare, Send, Plus, Trash2 } from 'lucide-react';

export default function TaskModal({ task, team, onClose, onUpdateTask, onDeleteTask }) {
  const [currentTask, setCurrentTask] = useState({ ...task });
  const [newComment, setNewComment] = useState('');
  const [newChecklistText, setNewChecklistText] = useState('');
  const [newTagText, setNewTagText] = useState('');

  const assignee = team.find(m => m.id === currentTask.assigneeId);

  const handleStatusChange = (status) => {
    const updated = { ...currentTask, status };
    setCurrentTask(updated);
    onUpdateTask(updated);
  };

  const handlePriorityChange = (priority) => {
    const updated = { ...currentTask, priority };
    setCurrentTask(updated);
    onUpdateTask(updated);
  };

  const handleAssigneeChange = (assigneeId) => {
    const updated = { ...currentTask, assigneeId };
    setCurrentTask(updated);
    onUpdateTask(updated);
  };

  const toggleChecklist = (clId) => {
    const updatedChecklist = currentTask.checklist.map(item => 
      item.id === clId ? { ...item, completed: !item.completed } : item
    );
    const updated = { ...currentTask, checklist: updatedChecklist };
    setCurrentTask(updated);
    onUpdateTask(updated);
  };

  const addChecklist = (e) => {
    e.preventDefault();
    if (!newChecklistText.trim()) return;
    const newItem = { id: 'cl-' + Date.now(), text: newChecklistText.trim(), completed: false };
    const updated = { ...currentTask, checklist: [...(currentTask.checklist || []), newItem] };
    setCurrentTask(updated);
    onUpdateTask(updated);
    setNewChecklistText('');
  };

  const removeChecklist = (clId) => {
    const updatedChecklist = currentTask.checklist.filter(item => item.id !== clId);
    const updated = { ...currentTask, checklist: updatedChecklist };
    setCurrentTask(updated);
    onUpdateTask(updated);
  };

  const addComment = (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    const commentObj = {
      id: 'cm-' + Date.now(),
      author: 'You (Admin)',
      text: newComment.trim(),
      time: 'Just now'
    };
    const updated = { ...currentTask, comments: [...(currentTask.comments || []), commentObj] };
    setCurrentTask(updated);
    onUpdateTask(updated);
    setNewComment('');
  };

  const addTag = (e) => {
    e.preventDefault();
    if (!newTagText.trim()) return;
    if (currentTask.tags?.includes(newTagText.trim())) return;
    const updated = { ...currentTask, tags: [...(currentTask.tags || []), newTagText.trim()] };
    setCurrentTask(updated);
    onUpdateTask(updated);
    setNewTagText('');
  };

  const removeTag = (tagToRemove) => {
    const updatedTags = currentTask.tags.filter(t => t !== tagToRemove);
    const updated = { ...currentTask, tags: updatedTags };
    setCurrentTask(updated);
    onUpdateTask(updated);
  };

  const priorityColors = {
    Urgent: 'bg-rose-100 text-rose-700 border-rose-200',
    High: 'bg-amber-100 text-amber-700 border-amber-200',
    Medium: 'bg-blue-100 text-blue-700 border-blue-200',
    Low: 'bg-slate-100 text-slate-700 border-slate-200'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center space-x-3">
            <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${priorityColors[currentTask.priority]}`}>
              {currentTask.priority} Priority
            </span>
            <span className="text-sm font-medium text-slate-500">ID: {currentTask.id}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => { onDeleteTask(currentTask.id); onClose(); }}
              className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
              title="Delete Task"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button 
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Title */}
          <div>
            <h2 className="text-xl font-bold text-slate-900">{currentTask.title}</h2>
            <p className="text-sm text-slate-600 mt-1">{currentTask.description}</p>
          </div>

          {/* Quick Properties Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
            {/* Status */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Status</label>
              <select 
                value={currentTask.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="w-full text-sm font-medium bg-white border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="backlog">Backlog</option>
                <option value="todo">To Do</option>
                <option value="in_progress">In Progress</option>
                <option value="review">Review</option>
                <option value="done">Done</option>
              </select>
            </div>

            {/* Assignee */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Assignee</label>
              <select 
                value={currentTask.assigneeId}
                onChange={(e) => handleAssigneeChange(e.target.value)}
                className="w-full text-sm font-medium bg-white border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {team.map(m => (
                  <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Priority</label>
              <select 
                value={currentTask.priority}
                onChange={(e) => handlePriorityChange(e.target.value)}
                className="w-full text-sm font-medium bg-white border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Urgent">Urgent</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>

          {/* Tags */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5" /> Tags & Labels
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {currentTask.tags?.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full">
                  {tag}
                  <button onClick={() => removeTag(tag)} className="hover:text-rose-600">×</button>
                </span>
              ))}
              <form onSubmit={addTag} className="flex items-center">
                <input 
                  type="text"
                  placeholder="+ Add tag..."
                  value={newTagText}
                  onChange={(e) => setNewTagText(e.target.value)}
                  className="text-xs px-3 py-1 border border-slate-200 rounded-full focus:outline-none focus:ring-1 focus:ring-indigo-500 w-28"
                />
              </form>
            </div>
          </div>

          {/* Checklist */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <CheckSquare className="w-3.5 h-3.5" /> Checklist ({currentTask.checklist?.filter(c => c.completed).length || 0}/{currentTask.checklist?.length || 0})
              </label>
            </div>
            <div className="space-y-2 mb-3">
              {currentTask.checklist?.map(item => (
                <div key={item.id} className="flex items-center justify-between group bg-slate-50 px-3 py-2 rounded-lg border border-slate-100">
                  <button 
                    onClick={() => toggleChecklist(item.id)}
                    className="flex items-center space-x-3 text-left flex-1"
                  >
                    {item.completed ? (
                      <CheckSquare className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <Square className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${item.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                      {item.text}
                    </span>
                  </button>
                  <button 
                    onClick={() => removeChecklist(item.id)}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 p-1 transition"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <form onSubmit={addChecklist} className="flex gap-2">
              <input 
                type="text"
                placeholder="Add checklist item..."
                value={newChecklistText}
                onChange={(e) => setNewChecklistText(e.target.value)}
                className="flex-1 text-sm px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition flex items-center gap-1">
                <Plus className="w-4 h-4" /> Add
              </button>
            </form>
          </div>

          {/* Time Tracking & Estimates */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-indigo-50/50 rounded-xl border border-indigo-100/50">
            <div className="flex items-center space-x-3">
              <div className="p-2.5 bg-indigo-100 text-indigo-700 rounded-lg">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Estimated Effort</p>
                <p className="text-base font-bold text-slate-900">{currentTask.estimatedHours || 0} Hours</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2.5 bg-emerald-100 text-emerald-700 rounded-lg">
                <CheckSquare className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Time Logged</p>
                <p className="text-base font-bold text-slate-900">{currentTask.loggedHours || 0} Hours</p>
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div className="border-t border-slate-100 pt-6">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-indigo-600" /> Activity & Comments
            </h3>
            <div className="space-y-3 mb-4">
              {currentTask.comments?.length === 0 && (
                <p className="text-xs text-slate-400 italic">No comments yet. Start the conversation below.</p>
              )}
              {currentTask.comments?.map(cm => (
                <div key={cm.id} className="bg-slate-50 p-3.5 rounded-xl border border-slate-100 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-800">{cm.author}</span>
                    <span className="text-[10px] text-slate-400">{cm.time}</span>
                  </div>
                  <p className="text-sm text-slate-600">{cm.text}</p>
                </div>
              ))}
            </div>

            <form onSubmit={addComment} className="flex gap-2">
              <input 
                type="text"
                placeholder="Write a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="flex-1 text-sm px-3.5 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button type="submit" className="px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-xl hover:bg-slate-800 transition flex items-center gap-1.5">
                <Send className="w-4 h-4" /> Send
              </button>
            </form>
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end">
          <button 
            onClick={onClose}
            className="px-5 py-2 bg-indigo-600 text-white font-medium text-sm rounded-xl hover:bg-indigo-700 transition"
          >
            Done
          </button>
        </div>

      </div>
    </div>
  );
}
